import { useState, useEffect } from 'react';
import { client } from '../../api/client';
import { useI18n } from '../../i18n';

interface NotificationPreference {
  key: string;
  labelSr: string;
  labelEn: string;
  descriptionSr: string;
  descriptionEn: string;
  dbKey: string;
}

const NOTIFICATION_TYPES: NotificationPreference[] = [
  {
    key: 'listing_approved',
    labelSr: 'Odobrenje oglasa',
    labelEn: 'Listing approval',
    descriptionSr: 'Kada administrator odobri vaš oglas',
    descriptionEn: 'When admin approves your listing',
    dbKey: 'notify_listing_approved_email',
  },
  {
    key: 'listing_rejected',
    labelSr: 'Odbijanje oglasa',
    labelEn: 'Listing rejection',
    descriptionSr: 'Kada administrator odbije vaš oglas',
    descriptionEn: 'When admin rejects your listing',
    dbKey: 'notify_listing_rejected_email',
  },
  {
    key: 'listing_expiring',
    labelSr: 'Istek oglasa',
    labelEn: 'Listing expiry',
    descriptionSr: 'Podsjetnik kada oglas uskoro ističe',
    descriptionEn: 'Reminder when listing is about to expire',
    dbKey: 'notify_listing_expiring_email',
  },
  {
    key: 'new_message',
    labelSr: 'Nove poruke',
    labelEn: 'New messages',
    descriptionSr: 'Kada dobijete novu poruku',
    descriptionEn: 'When you receive a new message',
    dbKey: 'notify_new_message_email',
  },
  {
    key: 'new_bid',
    labelSr: 'Nove ponude na aukciji',
    labelEn: 'New auction bids',
    descriptionSr: 'Kada neko da ponudu na vašu aukciju',
    descriptionEn: 'When someone bids on your auction',
    dbKey: 'notify_new_bid_email',
  },
  {
    key: 'property_match',
    labelSr: 'Poklapanje nekretnina',
    labelEn: 'Property matches',
    descriptionSr: 'Kada pronađemo nekretninu koja odgovara vašim kriterijumima',
    descriptionEn: 'When we find a property matching your criteria',
    dbKey: 'notify_property_match_email',
  },
  {
    key: 'appointment',
    labelSr: 'Zahtevi za razgledanje',
    labelEn: 'Appointment requests',
    descriptionSr: 'Zahtevi za zakazivanje razgledanja',
    descriptionEn: 'Requests for viewing appointments',
    dbKey: 'notify_appointment_email',
  },
  {
    key: 'tokens',
    labelSr: 'Tokeni',
    labelEn: 'Tokens',
    descriptionSr: 'Obaveštenja o tokenima (kupovina, potrošnja)',
    descriptionEn: 'Token notifications (purchase, usage)',
    dbKey: 'notify_tokens_email',
  },
  {
    key: 'system',
    labelSr: 'Sistemska obaveštenja',
    labelEn: 'System notifications',
    descriptionSr: 'Važna obaveštenja od administracije',
    descriptionEn: 'Important notifications from administration',
    dbKey: 'notify_system_email',
  },
  {
    key: 'favorite_update',
    labelSr: 'Promene omiljenih oglasa',
    labelEn: 'Favorite listing updates',
    descriptionSr: 'Kada se promeni cena ili drugi detalji oglasa koji pratite',
    descriptionEn: 'When price or other details change on listings you follow',
    dbKey: 'notify_favorite_update_email',
  },
];

// Newsletter is handled separately from email notification toggles
const NEWSLETTER_PREFERENCE: NotificationPreference = {
  key: 'newsletter',
  labelSr: 'Newsletter',
  labelEn: 'Newsletter',
  descriptionSr: 'Nedeljne novosti o tržištu nekretnina i novim funkcijama',
  descriptionEn: 'Weekly real estate market news and new features',
  dbKey: 'newsletter_subscribed',
};

interface NotificationPreferencesProps {
  defaultExpanded?: boolean;
}

export function NotificationPreferences({ defaultExpanded = false }: NotificationPreferencesProps) {
  const { language } = useI18n();
  const [preferences, setPreferences] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const res = await client.api.fetch('/api/notification-preferences');
      const data = await res.json();
      if (data.success && data.preferences) {
        setPreferences(data.preferences);
      } else {
        // Set defaults if no preferences found
        const defaults: Record<string, boolean> = {};
        NOTIFICATION_TYPES.forEach(type => {
          defaults[type.dbKey] = true;
        });
        setPreferences(defaults);
      }
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      // Set defaults on error
      const defaults: Record<string, boolean> = {};
      NOTIFICATION_TYPES.forEach(type => {
        defaults[type.dbKey] = true;
      });
      setPreferences(defaults);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (dbKey: string) => {
    const newValue = !preferences[dbKey];
    setPreferences(prev => ({ ...prev, [dbKey]: newValue }));
    setSaving(true);
    setMessage(null);

    try {
      const res = await client.api.fetch('/api/notification-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          [dbKey]: newValue ? 1 : 0,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setMessage({
          type: 'success',
          text: language === 'sr' ? 'Podešavanja sačuvana' : 'Settings saved',
        });
        // Clear success message after 2 seconds
        setTimeout(() => setMessage(null), 2000);
      } else {
        // Revert on error
        setPreferences(prev => ({ ...prev, [dbKey]: !newValue }));
        setMessage({
          type: 'error',
          text: data.error || (language === 'sr' ? 'Greška pri čuvanju' : 'Error saving'),
        });
      }
    } catch (err) {
      // Revert on error
      setPreferences(prev => ({ ...prev, [dbKey]: !newValue }));
      setMessage({
        type: 'error',
        text: language === 'sr' ? 'Greška pri čuvanju podešavanja' : 'Error saving settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const enableAll = async () => {
    setSaving(true);
    setMessage(null);
    const newPrefs: Record<string, number> = {};
    NOTIFICATION_TYPES.forEach(type => {
      newPrefs[type.dbKey] = 1;
    });

    try {
      const res = await client.api.fetch('/api/notification-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPrefs),
      });

      const data = await res.json();
      if (data.success) {
        const enabled: Record<string, boolean> = {};
        NOTIFICATION_TYPES.forEach(type => {
          enabled[type.dbKey] = true;
        });
        setPreferences(enabled);
        setMessage({
          type: 'success',
          text: language === 'sr' ? 'Sva obaveštenja uključena' : 'All notifications enabled',
        });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: language === 'sr' ? 'Greška pri čuvanju' : 'Error saving',
      });
    } finally {
      setSaving(false);
    }
  };

  const disableAll = async () => {
    setSaving(true);
    setMessage(null);
    const newPrefs: Record<string, number> = {};
    NOTIFICATION_TYPES.forEach(type => {
      newPrefs[type.dbKey] = 0;
    });

    try {
      const res = await client.api.fetch('/api/notification-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPrefs),
      });

      const data = await res.json();
      if (data.success) {
        const disabled: Record<string, boolean> = {};
        NOTIFICATION_TYPES.forEach(type => {
          disabled[type.dbKey] = false;
        });
        setPreferences(disabled);
        setMessage({
          type: 'success',
          text: language === 'sr' ? 'Sva email obaveštenja isključena' : 'All email notifications disabled',
        });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: language === 'sr' ? 'Greška pri čuvanju' : 'Error saving',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {language === 'sr' ? 'Email obaveštenja' : 'Email Notifications'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Podesite koje email notifikacije želite da primate' : 'Configure which email notifications you want to receive'}
            </p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <>
          <div className="px-6 pb-4 border-t border-slate-200 dark:border-slate-700">
            {/* Quick actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={enableAll}
                disabled={saving}
                className="text-sm px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors disabled:opacity-50"
              >
                {language === 'sr' ? 'Uključi sve' : 'Enable all'}
              </button>
              <button
                onClick={disableAll}
                disabled={saving}
                className="text-sm px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                {language === 'sr' ? 'Isključi sve' : 'Disable all'}
              </button>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`mx-6 mb-4 p-3 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}>
              {message.text}
            </div>
          )}

          <div className="px-6 pb-6">
            <div className="space-y-4">
              {NOTIFICATION_TYPES.map(type => (
                <div
                  key={type.key}
                  className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                >
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-slate-900 dark:text-white">
                      {language === 'sr' ? type.labelSr : type.labelEn}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {language === 'sr' ? type.descriptionSr : type.descriptionEn}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggle(type.dbKey)}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 ${
                      preferences[type.dbKey] ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences[type.dbKey] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="px-6 pb-6">
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {language === 'sr' ? 'Newsletter' : 'Newsletter'}
              </h3>
              <div className="flex items-center justify-between py-3">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-slate-900 dark:text-white">
                    {language === 'sr' ? NEWSLETTER_PREFERENCE.labelSr : NEWSLETTER_PREFERENCE.labelEn}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {language === 'sr' ? NEWSLETTER_PREFERENCE.descriptionSr : NEWSLETTER_PREFERENCE.descriptionEn}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle(NEWSLETTER_PREFERENCE.dbKey)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 ${
                    preferences[NEWSLETTER_PREFERENCE.dbKey] ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences[NEWSLETTER_PREFERENCE.dbKey] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              <strong>{language === 'sr' ? 'Napomena:' : 'Note:'}</strong>{' '}
              {language === 'sr'
                ? 'Čak i ako isključite email obaveštenja, sve notifikacije će i dalje biti vidljive na sajtu u sekciji Notifikacije.'
                : 'Even if you disable email notifications, all notifications will still be visible on the website in the Notifications section.'}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
